'use client'

import { Carousel, CarouselItem } from "@/components/elements/Carousel"
import { formatImageURL, formatImageURLGoogle, getActiveBanners } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import { useSession } from "next-auth/react"
import Image from "next/image"

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#f6f7f8" offset="20%" />
      <stop stop-color="#edeef1" offset="50%" />
      <stop stop-color="#f6f7f8" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#f6f7f8" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`

const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str)

const blurDataURL = `data:image/svg+xml;base64,${toBase64(shimmer(1600, 900))}`

type Campaign = {
  id: string
  name: string
  handle: string
  campaignBanner?: string
  customerOnly?: boolean
}

type Slide = { src: string, alt: string, link?: string }

export default function Hero({ vendor, activeCampaigns = [], overlay = false }: { vendor: any, activeCampaigns?: Campaign[], overlay?: boolean }) {
  const pathname = usePathname()
  const { data: session } = useSession()

  const liveVendorBanners = getActiveBanners(vendor?.banners || [])

  const liveCampaigns = activeCampaigns.filter((c) => c.campaignBanner && (!c.customerOnly || !!session?.user?.id))

  const campaignSlides: Slide[] = liveCampaigns.map((c) => ({
    src: formatImageURLGoogle(c.campaignBanner!),
    alt: c.name,
    link: navigateUrl(`/c/${c.handle}`, pathname),
  }))

  const bannerSlides: Slide[] = liveVendorBanners.map((b: any, i: number) => ({
    src: formatImageURL(b.image),
    alt: `${vendor?.name || 'Banner'} ${i + 1}`,
    link: b.link && b.link.trim() !== '' ? b.link : undefined,
  }))

  const slides = [...campaignSlides, ...bannerSlides]
  if (slides.length === 0) return null

  return (
    <section data-hero={overlay ? '' : undefined} className="relative w-full bg-muted">
      <Carousel className="max-w-none! cursor-grab active:cursor-grabbing" autoPlay={slides.length > 1} delay={6000}>
        {slides.map((slide, index) => {
          const inner = (
            <div className="relative w-full h-svh overflow-hidden select-none">
              <Image loader={myLoader} src={slide.src} fill draggable={false} sizes="100vw" placeholder="blur" blurDataURL={blurDataURL} className="object-cover" alt={slide.alt} priority={index === 0} />
            </div>
          )
          return (
            <CarouselItem key={`Slide-${index}`}>
              {slide.link ? (
                <a href={slide.link} aria-label={slide.alt} draggable={false} className="block">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </CarouselItem>
          )
        })}
      </Carousel>
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-black/25" />
      {overlay && <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-linear-to-b from-black/35 to-transparent" />}
    </section>
  )
}

const myLoader = ({ src, width, quality }: any) => {
  return `${src}?w=${width}&q=${quality || 75}`
}
